import React, { useState, useEffect, useRef, useCallback } from 'react'

import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player'

import 'react-h5-audio-player/lib/styles.css'
import defaultImage from '@/assets/images/default-image.png'
import { H4 } from '@/shared/components/atoms/H4'
import Icon from '@/shared/components/atoms/Icon'
import { cn } from '@/shared/utils/helpers'

import './player.css'

interface PlayerProps {
	src: string // Audio file URL
	cover?: string // Cover image URL
	title: string // Track title
	radioPrevText?: string // Radio/Playlist title
	radioTitle?: string // Radio/Playlist subtitle
	metadata?: string[] // Additional metadata (artist, album, etc.)
	onNext?: () => void // Next track handler
	onPrevious?: () => void // Previous track handler
	onPlay?: () => void // Play event handler
	onPause?: () => void // Pause event handler
	onEnded?: () => void // Track ended handler (for auto-advancing to next track)
	disableNext?: boolean // Disable next button
	disablePrevious?: boolean // Disable previous button
	showSkipControls?: boolean // Show skip controls
	showJumpControls?: boolean // Show jump controls
	autoPlay?: boolean // Auto play audio
	hasDefaultKeyBindings?: boolean // Has default key bindings
	className?: string // Optional class name
	cssVars?: Record<string, string> // Custom CSS variables
	hideVolume?: boolean // Hide volume controls inside player
	audioRef?: React.RefCallback<HTMLAudioElement> // Callback ref to expose audio element
	isFloat?: boolean // If true, the player is in float mode, which changes the layout of the player
	compact?: boolean // If true, shows only a play/pause button (useful for mobile)
}

const Player: React.FC<PlayerProps> = ({
	src,
	cover,
	title,
	radioPrevText,
	radioTitle = '',
	metadata = [],
	onNext,
	onPrevious,
	onPlay,
	onPause,
	onEnded,
	disableNext = false,
	disablePrevious = false,
	showSkipControls = true,
	showJumpControls = false,
	autoPlay = false,
	hasDefaultKeyBindings = false,
	className = '',
	cssVars = {},
	hideVolume = false,
	audioRef,
	isFloat = false,
	compact = false,
}) => {
	const [isPlaying, setIsPlaying] = useState(false)
	const [coverSrc, setCoverSrc] = useState<string>(cover || defaultImage)
	const [progress, setProgress] = useState(0) // Progress percentage (0-100)
	const playerRef = useRef<AudioPlayer>(null)

	// Toggle play/pause for compact mode
	const togglePlayPause = useCallback(() => {
		const audio = playerRef.current?.audio?.current
		if (!audio) return

		if (audio.paused) {
			audio.play()
		} else {
			audio.pause()
		}
	}, [])

	// Update progress bar for compact mode
	useEffect(() => {
		const audio = playerRef.current?.audio?.current
		if (!audio || !compact) return

		const updateProgress = () => {
			if (audio.duration) {
				setProgress((audio.currentTime / audio.duration) * 100)
			}
		}

		audio.addEventListener('timeupdate', updateProgress)
		audio.addEventListener('loadedmetadata', updateProgress)

		return () => {
			audio.removeEventListener('timeupdate', updateProgress)
			audio.removeEventListener('loadedmetadata', updateProgress)
		}
	}, [compact, src])

	// Update cover source if prop changes
	useEffect(() => {
		setCoverSrc(cover || defaultImage)
	}, [cover])

	// Handle cover image load error
	const handleImageError = () => {
		setCoverSrc(defaultImage)
	}

	// Create dynamic CSS variables
	const dynamicStyles = {
		'--player-bg-color': cssVars.backgroundColor || '--var(--background)',
		'--player-text-color': cssVars.textColor || 'var(--foreground)',
		'--player-primary-color': cssVars.primaryColor || 'var(--primary)',
		'--pause-background-color':
			cssVars.progressBarColor || 'var(--primary-foreground)',
		'--player-progress-bar-color': cssVars.progressBarColor || 'var(--muted)',
		'--player-progress-bar-filled-color':
			cssVars.progressBarColor || 'var(--foreground)',
		'--player-progress-indicator-color':
			cssVars.progressBarColor || 'var(--foreground)',
		'--player-border-radius': cssVars.borderRadius || 'var(--radius-md)',
		'--player-border': cssVars.border || `1px solid var(--border)`,
		'--player-time-font-size': cssVars.timeFontSize || '12px',
		'--player-subtitle-icon-size': cssVars.subtitleIconSize || '12px',
		'--player-play-size': cssVars.playSize || '40px',
		'--player-pause-size': cssVars.pauseSize || '40px',
		'--player-skip-size': cssVars.skipSize || '24px',
		'--player-volume-size': cssVars.volumeSize || '24px',
	} as React.CSSProperties

	// Create custom icons based on our existing Icon component
	const customIcons = {
		play: (
			<Icon
				name="play-circle-fill"
				width={cssVars.playSize || '40px'}
				height={cssVars.playSize || '40px'}
				color="var(--foreground)"
				hoverColor="var(--foreground)"
			/>
		),
		pause: (
			<Icon
				name="pause-circle-fill"
				width={cssVars.pauseSize || '40px'}
				height={cssVars.pauseSize || '40px'}
				color="var(--foreground)"
				hoverColor="var(--foreground)"
			/>
		),
		previous: (
			<Icon
				name="skip-start-fill"
				width={cssVars.skipSize || '24px'}
				height={cssVars.skipSize || '24px'}
				color="var(--foreground)"
				hoverColor="var(--foreground)"
				disabled={disablePrevious}
			/>
		),
		next: (
			<Icon
				name="skip-end-fill"
				width={cssVars.skipSize || '24px'}
				height={cssVars.skipSize || '24px'}
				color="var(--foreground)"
				hoverColor="var(--foreground)"
				disabled={disableNext}
			/>
		),
		volume: (
			<Icon
				name="volume-down"
				width={cssVars.volumeSize || '24px'}
				height={cssVars.volumeSize || '24px'}
				color="var(--foreground)"
				hoverColor="var(--foreground)"
			/>
		),
		volumeMute: (
			<Icon
				name="volume-mute-fill"
				width={cssVars.volumeSize || '24px'}
				height={cssVars.volumeSize || '24px'}
				color="var(--foreground)"
				hoverColor="var(--foreground)"
			/>
		),
	}

	const subtitleColor = 'var(--muted-foreground)'

	return (
		<div
			className={cn(
				'w-full flex-1 rounded-[var(--player-border-radius)] border-[var(--player-border)] py-2 text-[var(--player-text-color)] bg-[var(--player-bg-color)]',
				'mybt-player relative',
				className
			)}
			style={dynamicStyles}
		>
			{/* Player Content */}
			<div className="flex mb-1 items-center gap-4">
				{/* Album Cover */}
				<div className="w-[62px] h-[62px] shrink-0 rounded-2xl overflow-hidden">
					<img
						src={coverSrc}
						alt={title}
						onError={handleImageError}
						className="w-full h-full object-cover"
					/>
				</div>

				{/* Track Info */}
				<div className="flex flex-col min-w-0 lg:flex-[0_1_25%]">
					{/* Radio/Playlist Info */}
					<div className="items-center gap-2 mb-1 hidden">
						{radioPrevText && (
							<div className="font-heading text-[10px] font-semibold text-[var(--player-primary-color)] uppercase tracking-[1px]">
								{radioPrevText}
							</div>
						)}
						{radioPrevText && radioTitle && (
							<div className="w-px h-full min-h-[8px] bg-foreground " />
						)}
						{radioTitle && (
							<div className="items-center gap-1">
								<Icon
									name="music-note-beamed"
									color={subtitleColor}
									hoverColor={subtitleColor}
									width={cssVars.subtitleIconSize || '12px'}
									height={cssVars.subtitleIconSize || '12px'}
								/>
								<div className="font-heading text-[10px] font-medium text-muted-foreground uppercase hidden lg:block">
									{radioTitle}
								</div>
							</div>
						)}
					</div>

					{/* Track Title */}
					<H4 className="text-xs lg:text-base m-0 font-bold overflow-hidden text-ellipsis whitespace-nowrap">
						{title}
					</H4>

					{/* Metadata */}
					{metadata && metadata.length > 0 && (
						<div className="flex items-center text-xs text-[var(--player-text-color)] opacity-70 mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap">
							{/* Mobile: show only the first item (artist) */}
							<span className="lg:hidden overflow-hidden text-ellipsis">
								{metadata[0]}
							</span>
							{/* Desktop: show all metadata with separators */}
							<span className="hidden lg:flex items-center">
								{metadata.map((item, index) => (
									<React.Fragment key={index}>
										{index > 0 && <span className="mx-2 opacity-50">|</span>}
										<span className="overflow-hidden text-ellipsis">
											{item}
										</span>
									</React.Fragment>
								))}
							</span>
						</div>
					)}
				</div>

				{/* Compact Controls (mobile) */}
				{compact && (
					<div className="lg:hidden flex items-center gap-4 shrink-0 ml-8">
						{/* Previous Track Button */}
						{showSkipControls && (
							<button
								onClick={disablePrevious ? undefined : onPrevious}
								disabled={disablePrevious}
								className="hidden md:flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
								aria-label="Previous track"
							>
								<Icon
									name="skip-start-fill"
									width={cssVars.skipSize || '24px'}
									height={cssVars.skipSize || '24px'}
									color="var(--foreground)"
									hoverColor="var(--foreground)"
									disabled={disablePrevious}
								/>
							</button>
						)}

						{/* Play/Pause Button */}
						<button
							onClick={togglePlayPause}
							className="flex items-center justify-center"
							aria-label={isPlaying ? 'Pause' : 'Play'}
						>
							<Icon
								name={isPlaying ? 'pause-circle-fill' : 'play-circle-fill'}
								width={cssVars.playSize || '40px'}
								height={cssVars.playSize || '40px'}
								color="var(--foreground)"
								hoverColor="var(--foreground)"
							/>
						</button>

						{/* Next Track Button */}
						{showSkipControls && (
							<button
								onClick={disableNext ? undefined : onNext}
								disabled={disableNext}
								className="flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
								aria-label="Next track"
							>
								<Icon
									name="skip-end-fill"
									width={cssVars.skipSize || '24px'}
									height={cssVars.skipSize || '24px'}
									color="var(--foreground)"
									hoverColor="var(--foreground)"
									disabled={disableNext}
								/>
							</button>
						)}
					</div>
				)}

				{/* Full Audio Player (desktop or non-compact) */}
				<div
					className={cn('flex-1 px-6', compact && 'mybt-audio-player-hidden')}
				>
					<AudioPlayer
						className={cn('mybt-audio-player flex-1', isFloat && 'is-float')}
						src={src}
						ref={(instance) => {
							// Store internal ref
							playerRef.current = instance
							// Expose audio element to parent via callback ref
							if (audioRef && instance?.audio?.current) {
								audioRef(instance.audio.current)
							}
						}}
						autoPlay={autoPlay}
						autoPlayAfterSrcChange={false}
						onPlay={() => {
							setIsPlaying(true)
							onPlay?.()
						}}
						onPause={() => {
							setIsPlaying(false)
							onPause?.()
						}}
						onEnded={onEnded}
						showSkipControls={showSkipControls}
						showJumpControls={showJumpControls}
						customIcons={customIcons}
						showDownloadProgress={false}
						customProgressBarSection={
							hideVolume
								? [RHAP_UI.MAIN_CONTROLS]
								: [RHAP_UI.MAIN_CONTROLS, RHAP_UI.VOLUME]
						}
						customControlsSection={[
							RHAP_UI.CURRENT_TIME,
							RHAP_UI.PROGRESS_BAR,
							RHAP_UI.DURATION,
						]}
						onClickPrevious={disablePrevious ? undefined : onPrevious}
						onClickNext={disableNext ? undefined : onNext}
						customAdditionalControls={[]}
						customVolumeControls={hideVolume ? [] : [RHAP_UI.VOLUME_CONTROLS]}
						hasDefaultKeyBindings={hasDefaultKeyBindings}
						showFilledVolume={true}
					/>
				</div>
			</div>

			{/* Mobile Progress Bar (bottom of player) */}
			{compact && (
				<div className="lg:hidden absolute bottom-0 left-0 right-0 h-1 bg-muted/30 overflow-hidden rounded-b-[var(--player-border-radius)]">
					<div
						className="h-full bg-foreground transition-[width] duration-150 ease-linear"
						style={{ width: `${progress}%` }}
					/>
				</div>
			)}
		</div>
	)
}

export default Player
